import {
  Criterion,
  LogFacetParams,
  LogParams,
  LogState,
  SearchItem,
} from '@Types/log';
import { logSearchFields } from 'src/mapping/log.json';
import { escapeQueryString, surroundWildcardForAscii } from 'src/utils/chart';

import { globalStoreGetter } from '@Utils/globalStoreGetter';
import { convertLocalTimeToUtcTimeStr } from '@Utils/time';
import { t } from 'i18next';
import logConfig, {
  isoTimeFormat,
  mappingFields,
  quishingMappingFields,
  timeFields,
} from '../constants';
import { getQuarantineL10nObj, unwrapSearchFieldFuncMap } from '../shared';
import { MappingField, TimeField } from '../type';

export const filterEmptySearchItem = (searches: SearchItem[]) => {
  return searches.filter(
    (item: { field: string; values: string[] }) =>
      item.values.length && item.values[0] !== ''
  );
};

//Special field processing, refer to Log\LogFacet\index.tsx fieldFuncMap -> ddaaasRiskLevel
export const convertFacet = (facets: SearchItem[]): SearchItem[] => {
  return facets.map((facet) => {
    if (
      facet.field === 'ddaaasRiskLevel' &&
      ~facet.values.indexOf(t('quarantine.ddaaasRiskLevel.Unrated'))
    ) {
      // Unrated can be mapped to muti value
      return {
        ...facet,
        values: [
          ...facet.values,
          'unsupported file type in ROZ',
          'UNKNOWN',
          'Scan canceled',
          'RMS Decrypt Failed',
          'Network Timeout',
          'Network Circuit Break',
          'DDAaaS Other error',
        ],
      };
    } else {
      return facet;
    }
  });
};

export const convertCriteriaToSearchItems = (state: LogState): SearchItem[] => {
  const { criteria, type } = state;
  const orgsArr = globalStoreGetter.getOrgsArr();
  const quarantineL10nMap = getQuarantineL10nObj();
  const wrapFields = logConfig[type].wrapFields;
  const resultItems: SearchItem[] = [];
  const fieldValueMap: { [key: string]: string[] } = {};
  criteria.forEach((criterion) => {
    const { field, value } = criterion;
    let tempValues: string[] = [];
    // process map quishing, quishingMappingFields only support full match search and only support value 'QUISHING'
    if (quishingMappingFields.includes(field) && value === 'QUISHING') {
      tempValues.push('VAN_QUISHING.UMXX');
    }
    // process mergedFields
    const mergedFields = logConfig[type].mergedFields[field];
    if (Array.isArray(mergedFields) && mergedFields.length) {
      mergedFields.forEach((mergedField) => {
        // process map Fields
        if (mappingFields.includes(mergedField as MappingField)) {
          Object.keys(quarantineL10nMap[mergedField]).forEach((key) => {
            if (
              quarantineL10nMap[mergedField][key].toLowerCase() ===
              value.toLowerCase()
            ) {
              tempValues.push(key);
            }
          });
        }
        // process wrap Fields
        if (
          Object.keys(unwrapSearchFieldFuncMap).includes(mergedField) &&
          wrapFields.includes(field)
        ) {
          const wrapValues =
            unwrapSearchFieldFuncMap[
              mergedField as keyof typeof unwrapSearchFieldFuncMap
            ](value);
          tempValues = Array.from(new Set([...tempValues, ...wrapValues]));
        }
      });
    }
    if (timeFields.includes(field as TimeField)) {
      // process timeFields
      // convert value to date format
      const utcTime = convertLocalTimeToUtcTimeStr(value, isoTimeFormat);
      tempValues.push(utcTime);
      tempValues.push(utcTime);
    } else if (
      Object.keys(unwrapSearchFieldFuncMap).includes(field) &&
      wrapFields.includes(field)
    ) {
      // process unwrap search fields
      const wrapValues =
        unwrapSearchFieldFuncMap[
          field as keyof typeof unwrapSearchFieldFuncMap
        ](value);
      tempValues = Array.from(new Set([...tempValues, ...wrapValues]));
    } else if (mappingFields.includes(field as MappingField)) {
      // process mappingFields
      Object.keys(quarantineL10nMap[field]).forEach((key) => {
        if (
          quarantineL10nMap[field][key].toLowerCase() === value.toLowerCase()
        ) {
          tempValues.push(key);
        }
      });
    } else if (field === 'all') {
      Object.values(quarantineL10nMap).forEach((v) => {
        if (typeof v === 'string') {
          if (v.toLowerCase() === value.toLowerCase()) {
            tempValues.push(v);
          }
        } else {
          Object.entries(v).forEach(([key, val]) => {
            if (val && val.toLocaleString() === value.toLocaleString()) {
              tempValues.push(key);
            }
          });
        }
      });
    }

    // process query string, escape value
    if (!timeFields.includes(field as TimeField)) {
      if (field === 'companyGuid') {
        // process orgName
        const orgs = orgsArr.filter(
          (org) => org.value.toLowerCase() === value.toLowerCase()
        );

        if (orgs.length) {
          orgs.forEach((org) => (tempValues = tempValues.concat(org.key)));
        } else {
          tempValues = tempValues.concat('undefined');
        }
      } else if (field === 'mailDirection') {
        // process mailDirection
        if (
          value.toLowerCase() ===
          quarantineL10nMap['mailDirection']['in'].toLowerCase()
        ) {
          tempValues = tempValues.concat('in');
        } else if (
          value.toLowerCase() ===
          quarantineL10nMap['mailDirection']['out'].toLowerCase()
        ) {
          tempValues = tempValues.concat('out');
        }
      } else {
        // process common fields
        tempValues = tempValues.concat(value);
      }
    }

    if (type === 'urlTracking' && field === 'action') {
      tempValues = ['Blocked'];
    }

    // merge values
    // if criterion filed is same, combine the values
    if (fieldValueMap[field]) {
      const values = fieldValueMap[field];
      // remove the duplicate element in values array
      fieldValueMap[field] = Array.from(new Set([...values, ...tempValues]));
    } else {
      fieldValueMap[field] = Array.from(new Set([...tempValues]));
    }
  });
  //convert fieldValueMap to searchItem
  Object.keys(fieldValueMap).forEach((field) => {
    let searchType = 'queryString';
    let searchValues = fieldValueMap[field];

    const fieldItem = logSearchFields[type].find(
      (item) => item.field === field
    );
    if (fieldItem) {
      searchType = fieldItem.type;
    }

    if (field === 'all') {
      searchType = 'terms';
    }
    const escapeValues: string[] = [];
    if (searchType === 'queryString') {
      fieldValueMap[field].forEach((value) => {
        escapeValues.push(
          ...surroundWildcardForAscii(escapeQueryString(field, value), field)
        );
      });
      searchValues = escapeValues;
    }
    if (timeFields.includes(field as TimeField)) {
      searchType = 'range';
    }
    const item: SearchItem = {
      field,
      type: searchType,
      values: searchValues,
    };
    resultItems.push(item);
  });
  return resultItems;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const convertSearchItemsToCriteria = (
  searchItems: SearchItem[]
): Criterion[] => {
  const result: Criterion[] = [];
  searchItems.forEach((searchItem) => {
    const { field, values } = searchItem;
    const tempCriterion: Criterion = {
      field,
      label: t(`log.fields.${field}`),
      value: '',
      type: timeFields.includes(field as TimeField) ? 'range' : 'fuzzyMatching',
      focus: false,
    };
    values.forEach((value) => {
      const criterion = { ...tempCriterion };
      criterion.value = value;
      result.push(criterion);
    });
  });
  return result;
};

export const generateFacetListParams = (state: LogState): LogFacetParams => {
  const convertedCriteria: SearchItem[] = convertCriteriaToSearchItems(state);
  const convertedFacet: SearchItem[] = convertFacet(state.facets);

  const searches = filterEmptySearchItem([
    ...convertedCriteria,
    ...convertedFacet,
  ]);

  const facetParams: LogFacetParams = {
    type: state.type,
    searches: JSON.stringify(searches),
    timeRange: state.timeRange,
  };
  return facetParams;
};

export const generateContentListParams = (state: LogState): LogParams => {
  const convertedCriteria: SearchItem[] = convertCriteriaToSearchItems(state);
  const convertedFacet: SearchItem[] = convertFacet(state.facets);

  const searches = filterEmptySearchItem([
    ...convertedCriteria,
    ...convertedFacet,
  ]);

  const logParams: LogParams = {
    type: state.type,
    sort: state.sort,
    page: state.page,
    searches: JSON.stringify(searches),
    timeRange: state.timeRange,
  };
  return logParams;
};
